import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/Navigation';
import CosmicBackground from '../../components/CosmicBackground';
import ToolInterface from '../../components/ToolInterface';
import { getToolById } from '../../data/toolsData';

const LandingPageBuilderPage = () => {
  const tool = getToolById('landing-page-builder');
  const [businessName, setBusinessName] = useState('');
  const [product, setProduct] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [ctaText, setCtaText] = useState('');
  const [pageType, setPageType] = useState('lead-capture');
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState('');

  const pageTypes = [
    { id: 'lead-capture', name: 'Lead Capture' },
    { id: 'sales', name: 'Sales Page' },
    { id: 'webinar', name: 'Webinar Registration' },
    { id: 'app-download', name: 'App Download' },
    { id: 'course', name: 'Course Landing' }
  ];

  const handleGenerate = async () => {
    if (!businessName.trim() || !product.trim()) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      const generatedHTML = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${businessName} - ${product}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Arial', sans-serif; line-height: 1.6; color: #333; }
        .container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .hero { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 80px 0; text-align: center; }
        .hero h1 { font-size: 3rem; margin-bottom: 20px; font-weight: bold; }
        .hero p { font-size: 1.2rem; margin-bottom: 30px; max-width: 600px; margin-left: auto; margin-right: auto; }
        .cta-button { display: inline-block; background: #ff6b6b; color: white; padding: 15px 30px; text-decoration: none; border-radius: 50px; font-weight: bold; font-size: 1.1rem; transition: all 0.3s ease; }
        .cta-button:hover { background: #ff5252; transform: translateY(-2px); }
        .features { padding: 80px 0; background: #f8f9fa; }
        .features h2 { text-align: center; margin-bottom: 50px; font-size: 2.5rem; }
        .feature-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 30px; }
        .feature { background: white; padding: 30px; border-radius: 10px; box-shadow: 0 5px 15px rgba(0,0,0,0.1); text-align: center; }
        .feature h3 { margin-bottom: 15px; color: #667eea; }
        .testimonial { background: white; padding: 80px 0; text-align: center; }
        .testimonial blockquote { font-size: 1.3rem; font-style: italic; margin-bottom: 20px; max-width: 800px; margin-left: auto; margin-right: auto; }
        .final-cta { background: #2c3e50; color: white; padding: 80px 0; text-align: center; }
        .final-cta h2 { margin-bottom: 20px; font-size: 2.5rem; }
        .form-container { max-width: 400px; margin: 30px auto; }
        .form-container input, .form-container textarea { width: 100%; padding: 15px; margin-bottom: 15px; border: 1px solid #ddd; border-radius: 5px; font-size: 16px; }
        .form-container button { width: 100%; padding: 15px; background: #ff6b6b; color: white; border: none; border-radius: 5px; font-size: 18px; font-weight: bold; cursor: pointer; }
        @media (max-width: 768px) { .hero h1 { font-size: 2rem; } .hero p { font-size: 1rem; } }
    </style>
</head>
<body>
    <!-- Hero Section -->
    <section class="hero">
        <div class="container">
            <h1>${product}</h1>
            <p>Transform your ${targetAudience || 'business'} with our proven solution that delivers real results</p>
            <a href="#signup" class="cta-button">${ctaText || 'Get Started Today'}</a>
        </div>
    </section>

    <!-- Features Section -->
    <section class="features">
        <div class="container">
            <h2>Why Choose ${businessName}?</h2>
            <div class="feature-grid">
                <div class="feature">
                    <h3>🚀 Fast Results</h3>
                    <p>See improvements in your business within the first 30 days of implementation.</p>
                </div>
                <div class="feature">
                    <h3>💡 Expert Support</h3>
                    <p>Get guidance from industry experts who understand your challenges.</p>
                </div>
                <div class="feature">
                    <h3>📈 Proven System</h3>
                    <p>Our methodology has helped thousands of ${targetAudience || 'businesses'} succeed.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Testimonial Section -->
    <section class="testimonial">
        <div class="container">
            <blockquote>
                "${businessName} completely transformed how we approach our business. The results speak for themselves!"
            </blockquote>
            <p><strong>- Satisfied Customer</strong></p>
        </div>
    </section>

    <!-- Final CTA Section -->
    <section class="final-cta" id="signup">
        <div class="container">
            <h2>Ready to Get Started?</h2>
            <p>Join thousands of successful ${targetAudience || 'businesses'} today</p>
            
            <div class="form-container">
                <form>
                    <input type="text" placeholder="Your Name" required>
                    <input type="email" placeholder="Your Email" required>
                    ${pageType === 'lead-capture' ? '<input type="tel" placeholder="Your Phone Number">' : ''}
                    <button type="submit">${ctaText || 'Get Started Today'}</button>
                </form>
            </div>
        </div>
    </section>

    <script>
        // Simple form handling
        document.querySelector('form').addEventListener('submit', function(e) {
            e.preventDefault();
            alert('Thank you for your interest! We will contact you soon.');
        });
    </script>
</body>
</html>`;

      setResult(generatedHTML);
      setIsGenerating(false);
    }, 2500);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
  };

  const handleDownload = () => {
    const blob = new Blob([result], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${businessName.replace(/\s+/g, '-').toLowerCase()}-landing-page.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white relative overflow-hidden">
      <CosmicBackground />
      <Navigation />
      
      <section className="pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ToolInterface
              tool={tool}
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              result={result}
              onCopy={handleCopy}
              onDownload={handleDownload}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Business Name *
                  </label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g., Clarity with Jahx"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Product/Service *
                  </label>
                  <input
                    type="text"
                    value={product}
                    onChange={(e) => setProduct(e.target.value)}
                    placeholder="e.g., Business Automation Course"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Target Audience
                  </label>
                  <input
                    type="text"
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    placeholder="e.g., entrepreneurs, small business owners"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Page Type
                  </label>
                  <select
                    value={pageType}
                    onChange={(e) => setPageType(e.target.value)}
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  >
                    {pageTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-poppins font-medium text-gray-300 mb-2">
                    Call-to-Action Text
                  </label>
                  <input
                    type="text"
                    value={ctaText}
                    onChange={(e) => setCtaText(e.target.value)}
                    placeholder="e.g., Get Started Today, Download Now"
                    className="w-full bg-gray-700/50 border border-gray-600/50 rounded-lg px-4 py-3 text-white font-poppins focus:border-green-500 focus:outline-none"
                  />
                </div>
              </div>
            </ToolInterface>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPageBuilderPage;